import React, { useState, useEffect, useMemo, useCallback } from 'react';
import {
  Box,
  Button,
  Chip,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  FormControl,
  Autocomplete,
  IconButton,
  InputLabel,
  MenuItem,
  Select,
  Stack,
  TextField,
  Typography,
  useMediaQuery,
  useTheme,
} from '@mui/material';
import {
  Add as AddIcon,
  Close as CloseIcon,
  Delete as DeleteIcon,
  PlayArrow as PlayArrowIcon,
} from '@mui/icons-material';
import {
  ACTION_TYPE_OPTIONS,
  CONDITION_TYPE_OPTIONS,
  applyPlaySpeakerActionDefaults,
  applyHooterAlertActionDefaults,
  applySendMailActionDefaults,
  applySendWhatsAppActionDefaults,
  DEFAULT_IDLE_DURATION_SEC,
  defaultWorkstationConditions,
  desksByCameraFromRoi,
  emptyAction,
  parseMailRecipients,
  parseWhatsAppRecipients,
  getWhatsAppTemplatePreview,
  emptyCondition,
  formatEventLabel,
  getMailTemplatePlaceholderError,
  isFormValid,
  isValidEmail,
  isValidPhone,
  normalizePhone,
  LOGICAL_OPERATORS,
  OPERATOR_OPTIONS_BY_TYPE,
  canUseEmployeeCondition,
  canUseFirstCheckinCondition,
  canUseLastCheckoutTimeCondition,
  canUseViolationCondition,
  canUseUnmatchCondition,
  canUsePossibleMatchCondition,
  getCamerasForEvent,
  DEFAULT_LAST_CHECKOUT_TIME_VALUE,
  DEFAULT_TIME_VALUE,
  VIOLATION_VALUE_OPTIONS,
  UNMATCH_VALUE_OPTIONS,
  POSSIBLE_MATCH_VALUE_OPTIONS,
  isUnmatchYesValue,
  isPossibleMatchYesValue,
  ruleHasUnmatchYes,
  ruleHasPossibleMatchYes,
  syncActionTemplatesForConditions,
  syncWhatsAppTemplateForConditions,
  PERSON_SCOPE_CONDITION_TYPES,
  getConditionTypeOptionsForRow,
  getPriorCompanyEqualTo,
  getPriorPersonScopeIndex,
  applyWorkstationMailForStatus,
  formatWorkstationOptionLabel,
  isWorkstationEvent,
  OCCUPANCY_STATUS_OPTIONS,
  workstationOccupancyStatus,
} from './constants';
import { useAuth } from '../../contexts/AuthContext';
import {
  getAllROIConfigs,
  getCameraStreams,
  getCompanyPickerConfig,
  getPersons,
  getVendorPersons,
  getVisitorPersons,
  getIntrusionPersons,
  getWhatsAppTemplates,
  getAlertSounds,
  fetchAlertSoundBlob,
  playAlertSoundOnSpeaker,
  getSpeakers,
  getWorkstationCameras,
} from '../../utils/api';

const mapPersonList = (res) => {
  const persons = res?.persons || (Array.isArray(res) ? res : []);
  return persons
    .map((p) => ({
      id: String(p.person_id || p.id || ''),
      name: p.name || p.person_name || p.full_name || 'Unknown',
    }))
    .filter((p) => p.id);
};

const HOURS = Array.from({ length: 12 }, (_, i) => String(i + 1).padStart(2, '0'));
const MINUTES = Array.from({ length: 60 }, (_, i) => String(i).padStart(2, '0'));
const PERIODS = ['AM', 'PM'];
const parseTimeValue = (val) => {
  if (!val) return { hour: '09', minute: '00', period: 'AM' };
  const match = String(val).match(/^(\d{1,2}):(\d{2})(?::\d{2})?\s*(AM|PM)$/i);
  if (match) return { hour: String(match[1]).padStart(2, '0'), minute: match[2], period: match[3].toUpperCase() };
  return { hour: '09', minute: '00', period: 'AM' };
};

const formatTimeValue = ({ hour, minute, period }) => `${hour}:${minute}:00 ${period}`;

const MailRecipientsInput = ({ recipients = [], onChange }) => {
  const [input, setInput] = useState('');
  const [inputError, setInputError] = useState(false);

  const tryAdd = (raw) => {
    const email = String(raw || '').trim().toLowerCase();
    if (!email) {
      setInputError(false);
      return;
    }
    if (!isValidEmail(email)) {
      setInputError(true);
      return;
    }
    if (recipients.includes(email)) {
      setInput('');
      setInputError(false);
      return;
    }
    onChange([...recipients, email]);
    setInput('');
    setInputError(false);
  };

  const removeAt = (index) => {
    onChange(recipients.filter((_, i) => i !== index));
  };

  return (
    <Box>
      <TextField
        size="small"
        fullWidth
        label="Recipient emails"
        placeholder="Type email and press Enter"
        value={input}
        onChange={(e) => {
          setInput(e.target.value);
          if (inputError) setInputError(false);
        }}
        onKeyDown={(e) => {
          if (e.key === 'Enter' || e.key === ',') {
            e.preventDefault();
            tryAdd(input);
          } else if (e.key === 'Backspace' && !input && recipients.length) {
            removeAt(recipients.length - 1);
          }
        }}
        onBlur={() => tryAdd(input)}
        error={inputError}
        helperText={
          inputError
            ? 'Enter a valid email address'
            : 'Press Enter to add multiple emails'
        }
      />
      {recipients.length > 0 && (
        <Stack direction="row" flexWrap="wrap" gap={0.75} sx={{ mt: 1.25 }}>
          {recipients.map((email, emailIdx) => (
            <Chip
              key={`${email}-${emailIdx}`}
              label={email}
              size="small"
              onDelete={() => removeAt(emailIdx)}
              sx={{
                bgcolor: '#eef2ff',
                color: '#40518A',
                fontWeight: 500,
                '& .MuiChip-deleteIcon': { color: '#64748b' },
              }}
            />
          ))}
        </Stack>
      )}
    </Box>
  );
};

const WhatsAppRecipientsInput = ({ recipients = [], onChange }) => {
  const [input, setInput] = useState('');
  const [inputError, setInputError] = useState(false);

  const tryAdd = (raw) => {
    const phone = normalizePhone(raw);
    if (!phone) {
      setInputError(false);
      return;
    }
    if (!isValidPhone(phone)) {
      setInputError(true);
      return;
    }
    if (recipients.includes(phone)) {
      setInput('');
      setInputError(false);
      return;
    }
    onChange([...recipients, phone]);
    setInput('');
    setInputError(false);
  };

  const removeAt = (index) => {
    onChange(recipients.filter((_, i) => i !== index));
  };

  return (
    <Box>
      <TextField
        size="small"
        fullWidth
        label="Recipient phone numbers"
        placeholder="Type number with country code and press Enter"
        value={input}
        onChange={(e) => {
          setInput(e.target.value);
          if (inputError) setInputError(false);
        }}
        onKeyDown={(e) => {
          if (e.key === 'Enter' || e.key === ',') {
            e.preventDefault();
            tryAdd(input);
          } else if (e.key === 'Backspace' && !input && recipients.length) {
            removeAt(recipients.length - 1);
          }
        }}
        onBlur={() => tryAdd(input)}
        error={inputError}
        helperText={
          inputError
            ? 'Enter a valid phone number (10–15 digits, optional + prefix)'
            : 'Press Enter to add multiple numbers, e.g. 919876543210'
        }
      />
      {recipients.length > 0 && (
        <Stack direction="row" flexWrap="wrap" gap={0.75} sx={{ mt: 1.25 }}>
          {recipients.map((phone, phoneIdx) => (
            <Chip
              key={`${phone}-${phoneIdx}`}
              label={phone}
              size="small"
              onDelete={() => removeAt(phoneIdx)}
              sx={{
                bgcolor: '#eef2ff',
                color: '#40518A',
                fontWeight: 500,
                '& .MuiChip-deleteIcon': { color: '#64748b' },
              }}
            />
          ))}
        </Stack>
      )}
    </Box>
  );
};

const TimeInput = ({ value, onChange }) => {
  const parsed = parseTimeValue(value);
  const update = (field, val) => {
    onChange(formatTimeValue({ ...parsed, [field]: val }));
  };
  return (
    <Stack direction="row" spacing={0.75} alignItems="center">
      <FormControl size="small" sx={{ minWidth: 64 }}>
        <Select value={parsed.hour} onChange={(e) => update('hour', e.target.value)}>
          {HOURS.map((h) => <MenuItem key={h} value={h}>{h}</MenuItem>)}
        </Select>
      </FormControl>
      <Typography variant="body2" sx={{ fontWeight: 700 }}>:</Typography>
      <FormControl size="small" sx={{ minWidth: 64 }}>
        <Select value={parsed.minute} onChange={(e) => update('minute', e.target.value)}>
          {MINUTES.map((m) => <MenuItem key={m} value={m}>{m}</MenuItem>)}
        </Select>
      </FormControl>
      <FormControl size="small" sx={{ minWidth: 64 }}>
        <Select value={parsed.period} onChange={(e) => update('period', e.target.value)}>
          {PERIODS.map((p) => <MenuItem key={p} value={p}>{p}</MenuItem>)}
        </Select>
      </FormControl>
    </Stack>
  );
};

const RuleModal = ({ open, mode, form, onChange, onClose, onSave }) => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  const { permissions, isIntrusion } = useAuth();
  const [companies, setCompanies] = useState([]);
  const [employeesByCompany, setEmployeesByCompany] = useState({});
  const [vendors, setVendors] = useState([]);
  const [visitors, setVisitors] = useState([]);
  const [intrusions, setIntrusions] = useState([]);
  const [eventOptions, setEventOptions] = useState([]);
  const [cameraStreams, setCameraStreams] = useState([]);
  const [loadingCompanies, setLoadingCompanies] = useState(false);
  const [loadingEmployeeCompanyId, setLoadingEmployeeCompanyId] = useState(null);
  const [loadingVendors, setLoadingVendors] = useState(false);
  const [loadingVisitors, setLoadingVisitors] = useState(false);
  const [loadingIntrusions, setLoadingIntrusions] = useState(false);
  const [loadingEvents, setLoadingEvents] = useState(false);
  const [whatsappTemplates, setWhatsappTemplates] = useState([]);
  const [loadingWhatsAppTemplates, setLoadingWhatsAppTemplates] = useState(false);
  const [whatsappTemplatesError, setWhatsappTemplatesError] = useState('');
  const [alertSounds, setAlertSounds] = useState([]);
  const [alertSoundsConfigured, setAlertSoundsConfigured] = useState(true);
  const [loadingAlertSounds, setLoadingAlertSounds] = useState(false);
  const [alertSoundsError, setAlertSoundsError] = useState('');
  const [playingSound, setPlayingSound] = useState(null);
  const [playingSpeakerSound, setPlayingSpeakerSound] = useState(null);
  const [speakers, setSpeakers] = useState([]);
  const [loadingSpeakers, setLoadingSpeakers] = useState(false);
  const [speakersError, setSpeakersError] = useState('');
  const [workstationCameras, setWorkstationCameras] = useState([]);
  const [workstationRoi, setWorkstationRoi] = useState(null);

  const vendorManagementEnabled = permissions?.feature_flags?.vendor_management === true;
  const visitorManagementEnabled = permissions?.feature_flags?.visitor_management === true;
  const intrusionProfileEnabled = isIntrusion === true;
  const workstationMonitoringEnabled = permissions?.feature_flags?.workstation_monitoring === true;

  const camerasForEvent = useMemo(
    () => getCamerasForEvent(cameraStreams, form.event),
    [cameraStreams, form.event]
  );

  const desksByCamera = useMemo(
    () => desksByCameraFromRoi(workstationRoi, workstationCameras),
    [workstationRoi, workstationCameras]
  );

  const conditionTypeOptions = useMemo(
    () =>
      CONDITION_TYPE_OPTIONS.filter((opt) => {
        if (opt.value === 'vendor') return vendorManagementEnabled;
        if (opt.value === 'visitor') return visitorManagementEnabled;
        if (opt.value === 'intrusion') return intrusionProfileEnabled;
        return true;
      }),
    [vendorManagementEnabled, visitorManagementEnabled, intrusionProfileEnabled]
  );

  useEffect(() => {
    if (!open) return;
    let cancelled = false;

    const loadCompanies = async () => {
      setLoadingCompanies(true);
      try {
        const res = await getCompanyPickerConfig(permissions);
        if (cancelled) return;
        const configs = res?.configs || (res ? [res] : []);
        const seen = new Set();
        const list = [];
        configs.forEach((cfg) => {
          if (!cfg.company_id || seen.has(cfg.company_id)) return;
          seen.add(cfg.company_id);
          list.push({ company_id: cfg.company_id, company_name: cfg.company_name || 'Default Company' });
        });
        setCompanies(list);
      } catch {
        if (!cancelled) setCompanies([]);
      } finally {
        if (!cancelled) setLoadingCompanies(false);
      }
    };

    const loadEventOptions = async () => {
      setLoadingEvents(true);
      try {
        const [streamsResult, roiResult] = await Promise.allSettled([
          getCameraStreams(),
          getAllROIConfigs(),
        ]);
        if (cancelled) return;

        const eventTypeSet = new Set();

        if (streamsResult.status === 'fulfilled') {
          const items = streamsResult.value?.items || [];
          if (!cancelled) setCameraStreams(items);
          items.forEach((item) => {
            const type = String(item?.camera_type || '').trim().toUpperCase();
            if (type) eventTypeSet.add(type);
          });
        }

        if (roiResult.status === 'fulfilled') {
          const roiPayload = roiResult.value;
          if (!cancelled) {
            setWorkstationRoi(roiPayload?.WORKSTATION || null);
          }
          if (roiPayload && typeof roiPayload === 'object' && !Array.isArray(roiPayload)) {
            Object.keys(roiPayload).forEach((key) => {
              const roiEntry = roiPayload[key];
              const type = String(key || '').trim().toUpperCase();
              // Ignore metadata keys like updated_at/updated_by and only keep ROI type buckets.
              if (!type || !/^[A-Z0-9]+$/.test(type)) return;
              if (!roiEntry || typeof roiEntry !== 'object' || Array.isArray(roiEntry)) return;
              eventTypeSet.add(type);
            });
          }
        }

        const preferredOrder = ['IN', 'OUT', 'ACTIVITY', 'WORKSTATION'];
        if (workstationMonitoringEnabled) eventTypeSet.add('WORKSTATION');
        else eventTypeSet.delete('WORKSTATION');
        const options = [...eventTypeSet]
          .sort((a, b) => {
            const ai = preferredOrder.indexOf(a);
            const bi = preferredOrder.indexOf(b);
            if (ai >= 0 && bi >= 0) return ai - bi;
            if (ai >= 0) return -1;
            if (bi >= 0) return 1;
            return a.localeCompare(b);
          })
          .map((value) => ({ value, label: formatEventLabel(value) }));

        setEventOptions(options);
      } catch {
        if (!cancelled) {
          setEventOptions([]);
          setCameraStreams([]);
          setWorkstationRoi(null);
        }
      } finally {
        if (!cancelled) setLoadingEvents(false);
      }
    };

    const loadVendors = async () => {
      if (!vendorManagementEnabled) {
        setVendors([]);
        return;
      }
      setLoadingVendors(true);
      try {
        const res = await getVendorPersons(1000, 0, false);
        if (!cancelled) setVendors(mapPersonList(res));
      } catch {
        if (!cancelled) setVendors([]);
      } finally {
        if (!cancelled) setLoadingVendors(false);
      }
    };

    const loadVisitors = async () => {
      if (!visitorManagementEnabled) {
        setVisitors([]);
        return;
      }
      setLoadingVisitors(true);
      try {
        const res = await getVisitorPersons(1000, 0, false);
        if (!cancelled) setVisitors(mapPersonList(res));
      } catch {
        if (!cancelled) setVisitors([]);
      } finally {
        if (!cancelled) setLoadingVisitors(false);
      }
    };

    const loadIntrusions = async () => {
      if (!intrusionProfileEnabled) {
        setIntrusions([]);
        return;
      }
      setLoadingIntrusions(true);
      try {
        const res = await getIntrusionPersons(1000, 0, false);
        if (!cancelled) setIntrusions(mapPersonList(res));
      } catch {
        if (!cancelled) setIntrusions([]);
      } finally {
        if (!cancelled) setLoadingIntrusions(false);
      }
    };

    const loadWhatsAppTemplates = async () => {
      setLoadingWhatsAppTemplates(true);
      setWhatsappTemplatesError('');
      try {
        const res = await getWhatsAppTemplates({ page: 1, perPage: 100, status: 'approved' });
        if (cancelled) return;
        const items = (res?.data || []).filter((tpl) => String(tpl?.status || '').toUpperCase() === 'APPROVED');
        setWhatsappTemplates(items);
      } catch (err) {
        if (!cancelled) {
          setWhatsappTemplates([]);
          setWhatsappTemplatesError(err?.message || 'Failed to load WhatsApp templates');
        }
      } finally {
        if (!cancelled) setLoadingWhatsAppTemplates(false);
      }
    };

    const loadAlertSounds = async () => {
      setLoadingAlertSounds(true);
      setAlertSoundsError('');
      try {
        const res = await getAlertSounds();
        if (cancelled) return;
        setAlertSounds(Array.isArray(res?.sounds) ? res.sounds : []);
        setAlertSoundsConfigured(res?.configured !== false);
      } catch (err) {
        if (!cancelled) {
          setAlertSounds([]);
          setAlertSoundsConfigured(false);
          setAlertSoundsError(err?.message || 'Failed to load alert sounds');
        }
      } finally {
        if (!cancelled) setLoadingAlertSounds(false);
      }
    };

    const loadSpeakers = async () => {
      setLoadingSpeakers(true);
      setSpeakersError('');
      try {
        const res = await getSpeakers();
        if (cancelled) return;
        const list = Array.isArray(res?.speakers) ? res.speakers : [];
        setSpeakers(list.filter((sp) => sp?.id));
      } catch (err) {
        if (!cancelled) {
          setSpeakers([]);
          setSpeakersError(err?.message || 'Failed to load speakers');
        }
      } finally {
        if (!cancelled) setLoadingSpeakers(false);
      }
    };

    const loadWorkspaces = async () => {
      if (!workstationMonitoringEnabled) {
        if (!cancelled) setWorkstationCameras([]);
        return;
      }
      try {
        const res = await getWorkstationCameras();
        if (cancelled) return;
        const list = Array.isArray(res?.cameras) ? res.cameras : [];
        setWorkstationCameras(list.filter((cam) => cam?.camera_id));
      } catch {
        if (!cancelled) setWorkstationCameras([]);
      }
    };

    loadCompanies();
    loadVendors();
    loadVisitors();
    loadIntrusions();
    loadEventOptions();
    loadWhatsAppTemplates();
    loadAlertSounds();
    loadSpeakers();
    loadWorkspaces();
    return () => { cancelled = true; };
  }, [open, vendorManagementEnabled, visitorManagementEnabled, intrusionProfileEnabled, permissions, workstationMonitoringEnabled]);

  useEffect(() => {
    if (!open) {
      setEmployeesByCompany({});
      setLoadingEmployeeCompanyId(null);
      setVendors([]);
      setVisitors([]);
      setIntrusions([]);
      setCameraStreams([]);
      setWhatsappTemplates([]);
      setWhatsappTemplatesError('');
      setAlertSounds([]);
      setAlertSoundsError('');
      setPlayingSound(null);
      setPlayingSpeakerSound(null);
      setSpeakers([]);
      setSpeakersError('');
    }
  }, [open]);

  const loadEmployeesForCompany = async (companyId) => {
    const id = String(companyId || '').trim();
    if (!id || employeesByCompany[id]) return;
    setLoadingEmployeeCompanyId(id);
    try {
      const res = await getPersons(1000, 0, false, null, null, id);
      const persons = res?.persons || res?.data || [];
      const list = persons
        .map((p) => ({
          id: String(p.person_id || p.id || ''),
          name: p.name || p.person_name || p.full_name || 'Unknown',
        }))
        .filter((p) => p.id);
      setEmployeesByCompany((prev) => ({ ...prev, [id]: list }));
    } catch {
      setEmployeesByCompany((prev) => ({ ...prev, [id]: [] }));
    } finally {
      setLoadingEmployeeCompanyId(null);
    }
  };

  useEffect(() => {
    if (!open) return;
    form.conditions.forEach((cond, idx) => {
      if (cond.condition_type !== 'employee') return;
      const companyId = getPriorCompanyEqualTo(form.conditions, idx);
      if (companyId) loadEmployeesForCompany(companyId);
    });
  }, [open, form.conditions]);

  useEffect(() => {
    if (!open || loadingWhatsAppTemplates || whatsappTemplates.length === 0) return;
    const whatsappActions = form.actions.filter((action) => action.action_type === 'send_whatsapp');
    if (whatsappActions.length === 0) return;
    const needsDefault = whatsappActions.some(
      (action) => !action.whatsapp_template_db_id && !action.whatsapp_template_user_selected
    );
    if (!needsDefault) return;
    const synced = syncWhatsAppTemplateForConditions(form, whatsappTemplates);
    const before = JSON.stringify(
      whatsappActions.map((action) => ({
        dbId: action.whatsapp_template_db_id,
      }))
    );
    const after = JSON.stringify(
      (synced.actions || [])
        .filter((action) => action.action_type === 'send_whatsapp')
        .map((action) => ({
          dbId: action.whatsapp_template_db_id,
        }))
    );
    if (before !== after) {
      onChange(synced);
    }
    // Only auto-fill when CRM templates finish loading; do not re-sync on every form edit.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, loadingWhatsAppTemplates, whatsappTemplates]);

  const normalizeConditionsAfterChange = (conditions, event = form.event) =>
    conditions.map((c, i) => {
      if (c.condition_type === 'employee' && !canUseEmployeeCondition(conditions, i)) {
        return {
          ...emptyCondition(),
          id: c.id,
          logical_operator: c.logical_operator,
        };
      }
      if (c.condition_type === 'first_checkin' && !canUseFirstCheckinCondition(conditions, i, event)) {
        return {
          ...emptyCondition(),
          id: c.id,
          logical_operator: c.logical_operator,
        };
      }
      if (c.condition_type === 'last_checkout_time' && !canUseLastCheckoutTimeCondition(conditions, i, event)) {
        return {
          ...emptyCondition(),
          id: c.id,
          logical_operator: c.logical_operator,
        };
      }
      if (c.condition_type === 'violation' && !canUseViolationCondition(event)) {
        return {
          ...emptyCondition(),
          id: c.id,
          logical_operator: c.logical_operator,
        };
      }
      if (c.condition_type === 'unmatch' && !canUseUnmatchCondition(event)) {
        return {
          ...emptyCondition(),
          id: c.id,
          logical_operator: c.logical_operator,
        };
      }
      if (c.condition_type === 'possible_match' && !canUsePossibleMatchCondition(event)) {
        return {
          ...emptyCondition(),
          id: c.id,
          logical_operator: c.logical_operator,
        };
      }
      if (
        ruleHasUnmatchYes(conditions)
        && (
          PERSON_SCOPE_CONDITION_TYPES.includes(c.condition_type)
          || c.condition_type === 'first_checkin'
          || c.condition_type === 'last_checkout_time'
        )
      ) {
        return {
          ...emptyCondition(),
          id: c.id,
          logical_operator: c.logical_operator,
        };
      }
      if (c.condition_type === 'camera' && c.operator !== 'all') {
        const allowed = new Set(getCamerasForEvent(cameraStreams, event).map((cam) => cam.id));
        if (!event || !allowed.has(String(c.value || '').trim())) {
          return {
            ...c,
            value: '',
          };
        }
      }
      const workstationTypes = ['workspace', 'workstation', 'occupancy_status', 'idle_duration'];
      if (isWorkstationEvent(event) && !workstationTypes.includes(c.condition_type)) {
        return {
          ...emptyCondition(),
          id: c.id,
          logical_operator: c.logical_operator,
          condition_type: 'workspace',
          operator: 'equal_to',
          value: '',
        };
      }
      if (!isWorkstationEvent(event) && workstationTypes.includes(c.condition_type)) {
        return { ...emptyCondition(), id: c.id, logical_operator: c.logical_operator };
      }
      return c;
    });

  const clearDownstreamEmployeeValues = (conditions, fromIndex) =>
    conditions.map((c, i) => {
      if (i > fromIndex && c.condition_type === 'employee') {
        return { ...c, value: '' };
      }
      return c;
    });

  const setField = (field, value) => {
    if (field === 'event') {
      if (isWorkstationEvent(value)) {
        onChange({
          ...form,
          event: value,
          conditions: defaultWorkstationConditions(),
          actions: (form.actions || []).map((action) => (
            action.action_type === 'send_mail'
              ? applySendMailActionDefaults({ ...action, mail_subject: '', mail_body: '' }, value)
              : action
          )),
        });
        return;
      }
      if (isWorkstationEvent(form.event)) {
        onChange({
          ...form,
          event: value,
          conditions: [emptyCondition()],
          actions: (form.actions || []).map((action) => {
            if (action.action_type !== 'send_mail') return action;
            const subject = String(action.mail_subject || '').trim();
            const body = String(action.mail_body || '').trim();
            const wasWorkstationTemplate = subject.includes('{workspace}') || body.includes('{idle_duration}');
            return wasWorkstationTemplate
              ? applySendMailActionDefaults({ ...action, mail_subject: '', mail_body: '' })
              : action;
          }),
        });
        return;
      }
      const cleared = form.conditions.map((c) =>
        c.condition_type === 'camera' ? { ...c, value: '' } : c
      );
      const nextConditions = normalizeConditionsAfterChange(cleared, value);
      onChange({ ...form, event: value, conditions: nextConditions });
      return;
    }
    onChange({ ...form, [field]: value });
  };

  const updateCondition = (idx, patch) => {
    let next = form.conditions.map((c, i) => (i === idx ? { ...c, ...patch } : c));

    const affectsCompanyEligibility =
      patch.condition_type !== undefined ||
      patch.operator !== undefined ||
      patch.value !== undefined;

    if (affectsCompanyEligibility && next[idx]?.condition_type === 'company') {
      next = clearDownstreamEmployeeValues(next, idx);
    }

    if (
      affectsCompanyEligibility
      && next[idx]?.condition_type === 'workspace'
      && (patch.operator !== undefined || patch.value !== undefined)
    ) {
      next = next.map((c, i) => (
        i > idx && c.condition_type === 'workstation' && c.operator !== 'all'
          ? { ...c, value: '' }
          : c
      ));
    }

    if (
      (patch.value !== undefined || patch.operator !== undefined)
      && next[idx]?.condition_type === 'unmatch'
      && isUnmatchYesValue(next[idx]?.value)
    ) {
      next = next.map((c, i) => {
        if (
          i !== idx
          && (
            PERSON_SCOPE_CONDITION_TYPES.includes(c.condition_type)
            || c.condition_type === 'first_checkin'
            || c.condition_type === 'last_checkout_time'
          )
        ) {
          return {
            ...emptyCondition(),
            id: c.id,
            logical_operator: c.logical_operator,
          };
        }
        return c;
      });
    }

    next = normalizeConditionsAfterChange(next);
    const forcePossibleMatchTemplate =
      (patch.condition_type === 'possible_match'
        || (patch.value !== undefined && next[idx]?.condition_type === 'possible_match')
        || (patch.condition_type !== undefined && next[idx]?.condition_type === 'possible_match'))
      && isPossibleMatchYesValue(next[idx]?.value);
    let nextForm = { ...form, conditions: next };
    if (
      next[idx]?.condition_type === 'occupancy_status'
      && (patch.value !== undefined || patch.condition_type !== undefined)
    ) {
      nextForm = applyWorkstationMailForStatus(nextForm);
    }
    onChange(
      syncActionTemplatesForConditions(
        nextForm,
        whatsappTemplates,
        { force: forcePossibleMatchTemplate }
      )
    );
  };

  const addCondition = () => {
    const next = isWorkstationEvent(form.event)
      ? {
        ...emptyCondition(),
        condition_type: 'workspace',
        operator: 'equal_to',
        value: '',
      }
      : emptyCondition();
    setField('conditions', [...form.conditions, next]);
  };

  const removeCondition = (idx) => {
    if (form.conditions.length <= 1) return;
    const nextConditions = normalizeConditionsAfterChange(
      form.conditions.filter((_, i) => i !== idx)
    );
    onChange(
      syncActionTemplatesForConditions(
        { ...form, conditions: nextConditions },
        whatsappTemplates
      )
    );
  };

  const updateAction = (idx, patch) => {
    const next = form.actions.map((a, i) => i === idx ? { ...a, ...patch } : a);
    setField('actions', next);
  };

  const addAction = () => setField('actions', [...form.actions, emptyAction()]);

  const removeAction = (idx) => {
    if (form.actions.length <= 1) return;
    setField('actions', form.actions.filter((_, i) => i !== idx));
  };

  const previewAlertSound = async (filename) => {
    const name = String(filename || '').trim();
    if (!name || playingSound) return;
    setPlayingSound(name);
    try {
      const blob = await fetchAlertSoundBlob(name);
      const url = URL.createObjectURL(blob);
      const audio = new Audio(url);
      audio.onended = () => {
        URL.revokeObjectURL(url);
        setPlayingSound(null);
      };
      audio.onerror = () => {
        URL.revokeObjectURL(url);
        setPlayingSound(null);
      };
      await audio.play();
    } catch {
      setPlayingSound(null);
    }
  };

  const previewAlertSoundOnSpeaker = async (filename, speakerId) => {
    const name = String(filename || '').trim();
    const sid = String(speakerId || '').trim();
    if (!name || !sid || playingSpeakerSound) return;
    setPlayingSpeakerSound(name);
    try {
      await playAlertSoundOnSpeaker(name, sid);
    } finally {
      setPlayingSpeakerSound(null);
    }
  };

  const valid = useMemo(() => isFormValid(form), [form]);
  const mailPlaceholderError = useMemo(
    () => getMailTemplatePlaceholderError(form),
    [form]
  );

  const sectionLabelSx = {
    fontWeight: 700,
    fontSize: '0.85rem',
    color: '#40518A',
    mb: 1.5,
    display: 'block',
  };

  const renderSearchableSelect = useCallback(
    ({ label, loading, options, value, onSelect, emptyMessage, disabled = false }) => {
      const selected = options.find((o) => o.id === value) || null;
      if (loading) {
        return (
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, px: 1 }}>
            <CircularProgress size={14} />
            <Typography variant="caption">Loading…</Typography>
          </Box>
        );
      }
      return (
        <Autocomplete
          size="small"
          disabled={disabled}
          sx={{ width: '100%', minWidth: 220 }}
          options={options}
          value={selected}
          onChange={(_, newVal) => onSelect(newVal?.id || '')}
          getOptionLabel={(o) => o?.name || ''}
          isOptionEqualToValue={(o, v) => o.id === v.id}
          noOptionsText={emptyMessage}
          renderInput={(params) => <TextField {...params} label={label} />}
        />
      );
    },
    []
  );

  const renderCompanySelect = useCallback(
    ({ loading, options, value, onSelect, disabled = false }) => {
      const selected = options.find((o) => o.company_id === value) || null;
      if (loading) {
        return (
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, px: 1 }}>
            <CircularProgress size={14} />
            <Typography variant="caption">Loading…</Typography>
          </Box>
        );
      }
      return (
        <Autocomplete
          size="small"
          disabled={disabled}
          sx={{ width: '100%', minWidth: 220 }}
          options={options}
          value={selected}
          onChange={(_, newVal) => onSelect(newVal?.company_id || '')}
          getOptionLabel={(o) => o?.company_name || ''}
          isOptionEqualToValue={(o, v) => o.company_id === v.company_id}
          noOptionsText="No companies found"
          renderInput={(params) => <TextField {...params} label="Select company" />}
        />
      );
    },
    []
  );

  const renderPersonSelect = renderSearchableSelect;

  const renderConditionValue = (cond, idx) => {
    if (cond.operator === 'all') return null;

    if (cond.condition_type === 'camera') {
      if (!form.event) {
        return (
          <Typography variant="caption" color="text.secondary" sx={{ px: 1, py: 1 }}>
            Select an event first
          </Typography>
        );
      }
      return renderSearchableSelect({
        label: 'Select camera',
        loading: false,
        disabled: camerasForEvent.length === 0,
        options: camerasForEvent,
        value: cond.value,
        onSelect: (val) => updateCondition(idx, { value: val }),
        emptyMessage: 'No cameras configured for this event',
      });
    }

    if (cond.condition_type === 'company') {
      return renderCompanySelect({
        loading: loadingCompanies,
        options: companies,
        value: cond.value || '',
        onSelect: (val) => updateCondition(idx, { value: val }),
      });
    }

    if (cond.condition_type === 'employee') {
      const companyId = getPriorCompanyEqualTo(form.conditions, idx);
      const employees = companyId ? (employeesByCompany[companyId] || []) : [];
      const loadingEmployees = loadingEmployeeCompanyId === companyId;

      if (!companyId) {
        return (
          <Typography variant="caption" color="text.secondary" sx={{ px: 1, py: 1 }}>
            Add a Company condition (Equal to) above first
          </Typography>
        );
      }

      return renderSearchableSelect({
        label: 'Select employee',
        loading: loadingEmployees,
        options: employees,
        value: cond.value,
        onSelect: (val) => updateCondition(idx, { value: val }),
        emptyMessage: 'No employees for this company',
      });
    }

    if (cond.condition_type === 'vendor') {
      return renderPersonSelect({
        label: 'Select vendor',
        loading: loadingVendors,
        options: vendors,
        value: cond.value,
        onSelect: (val) => updateCondition(idx, { value: val }),
        emptyMessage: vendorManagementEnabled ? 'No vendors found' : 'Vendor management is disabled',
      });
    }

    if (cond.condition_type === 'visitor') {
      return renderPersonSelect({
        label: 'Select visitor',
        loading: loadingVisitors,
        options: visitors,
        value: cond.value,
        onSelect: (val) => updateCondition(idx, { value: val }),
        emptyMessage: visitorManagementEnabled ? 'No visitors found' : 'Visitor management is disabled',
      });
    }

    if (cond.condition_type === 'intrusion') {
      return renderPersonSelect({
        label: 'Select intrusion profile',
        loading: loadingIntrusions,
        options: intrusions,
        value: cond.value,
        onSelect: (val) => updateCondition(idx, { value: val }),
        emptyMessage: intrusionProfileEnabled ? 'No intrusion profiles found' : 'Intrusion access is disabled',
      });
    }

    if (cond.condition_type === 'time') {
      return (
        <Box sx={{ width: '100%', minWidth: 220 }}>
          <TimeInput
            value={cond.value}
            onChange={(val) => updateCondition(idx, { value: val })}
          />
        </Box>
      );
    }

    if (cond.condition_type === 'first_checkin') {
      return (
        <FormControl size="small" sx={{ width: '100%', minWidth: 120 }}>
          <InputLabel>Value</InputLabel>
          <Select
            label="Value"
            value={cond.value || 'true'}
            onChange={(e) => updateCondition(idx, { value: e.target.value })}
          >
            {VIOLATION_VALUE_OPTIONS.map((opt) => (
              <MenuItem key={opt.value} value={opt.value}>{opt.label}</MenuItem>
            ))}
          </Select>
        </FormControl>
      );
    }

    if (cond.condition_type === 'last_checkout_time') {
      return (
        <Box sx={{ width: '100%', minWidth: 220 }}>
          <TimeInput
            value={cond.value || DEFAULT_LAST_CHECKOUT_TIME_VALUE}
            onChange={(val) => updateCondition(idx, { value: val })}
          />
        </Box>
      );
    }

    if (cond.condition_type === 'violation') {
      return (
        <FormControl size="small" sx={{ width: '100%', minWidth: 120 }}>
          <InputLabel>Value</InputLabel>
          <Select
            label="Value"
            value={cond.value || 'true'}
            onChange={(e) => updateCondition(idx, { value: e.target.value })}
          >
            {VIOLATION_VALUE_OPTIONS.map((opt) => (
              <MenuItem key={opt.value} value={opt.value}>{opt.label}</MenuItem>
            ))}
          </Select>
        </FormControl>
      );
    }

    if (cond.condition_type === 'unmatch') {
      return (
        <FormControl size="small" sx={{ width: '100%', minWidth: 120 }}>
          <InputLabel>Value</InputLabel>
          <Select
            label="Value"
            value={cond.value || 'true'}
            onChange={(e) => updateCondition(idx, { value: e.target.value })}
          >
            {UNMATCH_VALUE_OPTIONS.map((opt) => (
              <MenuItem key={opt.value} value={opt.value}>{opt.label}</MenuItem>
            ))}
          </Select>
        </FormControl>
      );
    }

    if (cond.condition_type === 'possible_match') {
      return (
        <FormControl size="small" sx={{ width: '100%', minWidth: 120 }}>
          <InputLabel>Value</InputLabel>
          <Select
            label="Value"
            value={cond.value || 'true'}
            onChange={(e) => updateCondition(idx, { value: e.target.value })}
          >
            {POSSIBLE_MATCH_VALUE_OPTIONS.map((opt) => (
              <MenuItem key={opt.value} value={opt.value}>{opt.label}</MenuItem>
            ))}
          </Select>
        </FormControl>
      );
    }

    if (cond.condition_type === 'workspace') {
      const options = workstationCameras.map((cam) => ({
        id: String(cam.camera_id),
        name: cam.label || cam.camera_id,
      }));
      return renderSearchableSelect({
        label: 'Select workspace',
        loading: false,
        options,
        value: cond.value,
        onSelect: (val) => updateCondition(idx, { value: val }),
        emptyMessage: 'No workspaces assigned',
      });
    }

    if (cond.condition_type === 'workstation') {
      const prior = [...form.conditions]
        .slice(0, idx)
        .reverse()
        .find((c) => c.condition_type === 'workspace' && c.operator === 'equal_to' && c.value);
      if (!prior) {
        return (
          <Typography variant="caption" color="text.secondary" sx={{ px: 1, py: 1 }}>
            Add a Workspace condition (Equal to) above first
          </Typography>
        );
      }
      const deskIds = desksByCamera[String(prior.value)] || [];
      const options = deskIds.map((id) => ({
        id: String(id),
        name: formatWorkstationOptionLabel(id),
      }));
      return renderSearchableSelect({
        label: 'Select workstation',
        loading: false,
        options,
        value: cond.value,
        onSelect: (val) => updateCondition(idx, { value: val }),
        emptyMessage: 'No workstations drawn for this workspace',
      });
    }

    if (cond.condition_type === 'occupancy_status') {
      return (
        <FormControl size="small" sx={{ width: '100%', minWidth: 160 }}>
          <InputLabel>Status</InputLabel>
          <Select
            label="Status"
            value={cond.value || 'idle'}
            onChange={(e) => updateCondition(idx, { value: e.target.value })}
          >
            {OCCUPANCY_STATUS_OPTIONS.map((opt) => (
              <MenuItem key={opt.value} value={opt.value}>{opt.label}</MenuItem>
            ))}
          </Select>
        </FormControl>
      );
    }

    if (cond.condition_type === 'idle_duration') {
      const seconds = Number(cond.value);
      const minutes = Number.isFinite(seconds) && seconds > 0
        ? Math.max(1, Math.round(seconds / 60))
        : 10;
      return (
        <TextField
          size="small"
          type="number"
          label="Minutes"
          value={minutes}
          inputProps={{ min: 1, step: 1 }}
          onChange={(e) => {
            const mins = Math.max(1, Number(e.target.value) || 1);
            updateCondition(idx, { value: String(mins * 60) });
          }}
          sx={{ width: '100%', minWidth: 160 }}
        />
      );
    }

    return null;
  };

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="md"
      fullWidth
      fullScreen={isMobile}
      PaperProps={{
        sx: {
          borderRadius: isMobile ? 0 : 3,
          maxWidth: isMobile ? '100%' : 760,
          width: '100%',
          m: isMobile ? 0 : undefined,
        },
      }}
    >
      <DialogTitle
        sx={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          fontWeight: 700,
          color: '#40518A',
          pb: 1,
          px: { xs: 2, sm: 3 },
          fontSize: { xs: '1.05rem', sm: '1.25rem' },
        }}
      >
        {mode === 'edit' ? 'Edit Rule' : 'Add Automation Rule'}
        <IconButton onClick={onClose} size="small">
          <CloseIcon />
        </IconButton>
      </DialogTitle>

      <DialogContent dividers sx={{ px: { xs: 2, sm: 3 }, py: { xs: 2, sm: 2.5 } }}>
        <Stack spacing={2.5}>
          {/* Rule Name */}
          <TextField
            label="Rule Name"
            placeholder="Enter rule name"
            value={form.name}
            onChange={(e) => setField('name', e.target.value)}
            size="small"
            fullWidth
            required
          />

          {/* Description */}
          <TextField
            label="Description"
            placeholder="Enter rule description"
            value={form.description}
            onChange={(e) => setField('description', e.target.value)}
            size="small"
            fullWidth
            multiline
            minRows={2}
          />

          {/* Event */}
          <FormControl size="small" fullWidth required>
            <InputLabel shrink>Event</InputLabel>
            <Select
              label="Event"
              value={form.event || ''}
              displayEmpty
              renderValue={(selected) => {
                if (!selected) return '';
                const opt = eventOptions.find((o) => o.value === selected);
                return opt?.label ?? formatEventLabel(selected);
              }}
              onChange={(e) => setField('event', e.target.value)}
              disabled={loadingEvents || eventOptions.length === 0}
            >
              {eventOptions.map((opt) => (
                <MenuItem key={opt.value} value={opt.value}>{opt.label}</MenuItem>
              ))}
            </Select>
          </FormControl>

          <Divider />

          {/* Conditions */}
          <Box>
            <Typography sx={sectionLabelSx}>Conditions</Typography>
            <Box
              sx={{
                border: '1px solid',
                borderColor: 'divider',
                borderRadius: 2,
                overflow: 'hidden',
              }}
            >
              {form.conditions.map((cond, idx) => (
                <Box key={cond.id}>
                  {/* Condition row */}
                  <Box sx={{ p: 2 }}>
                    <Box
                      sx={{
                        display: 'grid',
                        gridTemplateColumns: {
                          xs: '1fr',
                          sm: 'minmax(128px, 1fr) minmax(148px, 1fr) minmax(240px, 2fr) auto',
                        },
                        gap: 1.5,
                        alignItems: 'start',
                      }}
                    >
                      <FormControl size="small" sx={{ width: '100%' }}>
                        <InputLabel>Attribute</InputLabel>
                        <Select
                          label="Attribute"
                          value={cond.condition_type}
                          onChange={(e) => {
                            const newType = e.target.value;
                            const newOperator = OPERATOR_OPTIONS_BY_TYPE[newType][0].value;
                            updateCondition(idx, {
                              condition_type: newType,
                              operator: newOperator,
                              value:
                                newType === 'first_checkin'
                                  ? 'true'
                                  : newType === 'last_checkout_time'
                                    ? DEFAULT_LAST_CHECKOUT_TIME_VALUE
                                    : newType === 'time'
                                      ? DEFAULT_TIME_VALUE
                                      : newType === 'violation'
                                        ? 'true'
                                        : newType === 'unmatch'
                                          ? 'true'
                                          : newType === 'possible_match'
                                            ? 'true'
                                            : newType === 'occupancy_status'
                                              ? 'idle'
                                              : newType === 'idle_duration'
                                                ? DEFAULT_IDLE_DURATION_SEC
                                                : '',
                            });
                          }}
                        >
                          {getConditionTypeOptionsForRow(
                            form.conditions,
                            idx,
                            conditionTypeOptions,
                            form.event
                          ).map((opt) => (
                            <MenuItem key={opt.value} value={opt.value}>
                              {opt.value === 'idle_duration' && workstationOccupancyStatus(form.conditions) === 'occupied'
                                ? 'Occupied for'
                                : opt.label}
                            </MenuItem>
                          ))}
                        </Select>
                      </FormControl>

                      <FormControl size="small" sx={{ width: '100%' }}>
                        <InputLabel>Operator</InputLabel>
                        <Select
                          label="Operator"
                          value={cond.operator}
                          onChange={(e) => updateCondition(idx, {
                            operator: e.target.value,
                            value:
                              cond.condition_type === 'first_checkin'
                                ? (cond.value || 'true')
                                : cond.condition_type === 'last_checkout_time'
                                  ? (cond.value || DEFAULT_LAST_CHECKOUT_TIME_VALUE)
                                  : cond.condition_type === 'time'
                                    ? (cond.value || DEFAULT_TIME_VALUE)
                                    : cond.condition_type === 'violation'
                                      ? (cond.value || 'true')
                                      : cond.condition_type === 'unmatch'
                                        ? (cond.value || 'true')
                                        : cond.condition_type === 'possible_match'
                                          ? (cond.value || 'true')
                                          : ['workspace', 'workstation', 'occupancy_status', 'idle_duration'].includes(cond.condition_type)
                                            ? cond.value
                                            : '',
                          })}
                        >
                          {(OPERATOR_OPTIONS_BY_TYPE[cond.condition_type] || []).map((opt) => (
                            <MenuItem key={opt.value} value={opt.value}>{opt.label}</MenuItem>
                          ))}
                        </Select>
                      </FormControl>

                      <Box sx={{ width: '100%', minWidth: { xs: '100%', sm: 240 } }}>
                        {renderConditionValue(cond, idx)}
                      </Box>

                      {form.conditions.length > 1 ? (
                        <IconButton
                          size="small"
                          onClick={() => removeCondition(idx)}
                          sx={{ color: '#ef4444', mt: 0.5, justifySelf: { xs: 'flex-end', sm: 'center' } }}
                        >
                          <DeleteIcon fontSize="small" />
                        </IconButton>
                      ) : (
                        <Box sx={{ display: { xs: 'none', sm: 'block' } }} />
                      )}
                    </Box>
                  </Box>

                  {/* AND / OR connector between conditions */}
                  {idx < form.conditions.length - 1 && (
                    <Box
                      sx={{
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        py: 0.5,
                        bgcolor: '#f8fafc',
                        borderTop: '1px solid',
                        borderBottom: '1px solid',
                        borderColor: 'divider',
                      }}
                    >
                      <FormControl size="small" sx={{ minWidth: 80 }}>
                        <Select
                          value={cond.logical_operator || 'AND'}
                          onChange={(e) => updateCondition(idx, { logical_operator: e.target.value })}
                          sx={{ fontWeight: 700, fontSize: '0.8rem' }}
                        >
                          {LOGICAL_OPERATORS.map((op) => (
                            <MenuItem key={op} value={op} sx={{ fontWeight: 700 }}>{op}</MenuItem>
                          ))}
                        </Select>
                      </FormControl>
                    </Box>
                  )}
                </Box>
              ))}

              {/* Add Condition */}
              <Box sx={{ p: 1.5, bgcolor: '#f8fafc', borderTop: '1px solid', borderColor: 'divider' }}>
                <Button
                  size="small"
                  startIcon={<AddIcon />}
                  onClick={addCondition}
                  sx={{ textTransform: 'none', fontWeight: 600, color: '#40518A' }}
                >
                  Add Condition
                </Button>
              </Box>
            </Box>
          </Box>

          <Divider />

          {/* Actions */}
          <Box>
            <Typography sx={sectionLabelSx}>Actions</Typography>
            <Box
              sx={{
                border: '1px solid',
                borderColor: 'divider',
                borderRadius: 2,
                overflow: 'hidden',
              }}
            >
              {form.actions.map((action, idx) => (
                  <Box
                    key={action.id}
                    sx={{
                      p: 2,
                      borderBottom: idx < form.actions.length - 1 ? '1px solid' : 'none',
                      borderColor: 'divider',
                    }}
                  >
                    <Stack direction="row" spacing={1.5} alignItems="center" sx={{ mb: action.action_type === 'send_mail' ? 0 : 0 }}>
                      <FormControl size="small" sx={{ minWidth: 220 }}>
                        <InputLabel id={`action-label-${action.id}`} shrink>
                          Action
                        </InputLabel>
                        <Select
                          labelId={`action-label-${action.id}`}
                          label="Action"
                          value={action.action_type || ''}
                          displayEmpty
                          renderValue={(selected) => {
                            const opt = ACTION_TYPE_OPTIONS.find((o) => o.value === selected);
                            return opt?.label ?? '';
                          }}
                          onChange={(e) => {
                            const nextType = e.target.value;
                            if (nextType === 'send_mail') {
                              const nextActions = form.actions.map((a, i) =>
                                i === idx
                                  ? applySendMailActionDefaults({
                                    ...action,
                                    value: '',
                                    recipients: [],
                                    whatsapp_template_id: '',
                                    whatsapp_template_name: '',
                                    whatsapp_template_db_id: null,
                                  }, form.event, form.conditions)
                                  : a
                              );
                              onChange(
                                syncActionTemplatesForConditions(
                                  { ...form, actions: nextActions },
                                  whatsappTemplates,
                                  { force: ruleHasPossibleMatchYes(form.conditions) }
                                )
                              );
                              return;
                            }
                            if (nextType === 'send_whatsapp') {
                              const nextActions = form.actions.map((a, i) =>
                                i === idx
                                  ? applySendWhatsAppActionDefaults({
                                    ...action,
                                    value: '',
                                    recipients: [],
                                    mail_subject: '',
                                    mail_body: '',
                                  })
                                  : a
                              );
                              onChange(
                                syncActionTemplatesForConditions(
                                  { ...form, actions: nextActions },
                                  whatsappTemplates,
                                  { force: ruleHasPossibleMatchYes(form.conditions) }
                                )
                              );
                              return;
                            }
                            if (nextType === 'play_speaker') {
                              updateAction(idx, applyPlaySpeakerActionDefaults({
                                ...action,
                                value: '',
                                speaker_id: '',
                              }));
                              return;
                            }
                            if (nextType === 'hooter_alert') {
                              updateAction(idx, applyHooterAlertActionDefaults(action));
                              return;
                            }
                            updateAction(idx, {
                              action_type: nextType,
                              value: '',
                              recipients: [],
                              mail_subject: '',
                              mail_body: '',
                              whatsapp_template_id: '',
                              whatsapp_template_name: '',
                              whatsapp_template_db_id: null,
                            });
                          }}
                        >
                          {ACTION_TYPE_OPTIONS.map((opt) => (
                            <MenuItem key={opt.value} value={opt.value}>{opt.label}</MenuItem>
                          ))}
                        </Select>
                      </FormControl>

                      {form.actions.length > 1 && (
                        <IconButton
                          size="small"
                          onClick={() => removeAction(idx)}
                          sx={{ color: '#ef4444', ml: 'auto' }}
                        >
                          <DeleteIcon fontSize="small" />
                        </IconButton>
                      )}
                    </Stack>

                    {action.action_type === 'send_mail' && (
                      <Stack spacing={2} sx={{ mt: 2 }}>
                        <MailRecipientsInput
                          recipients={parseMailRecipients(action.recipients ?? action.value)}
                          onChange={(recipients) => updateAction(idx, { recipients })}
                        />

                        <Box
                          sx={{
                            p: 2,
                            borderRadius: 1.5,
                            bgcolor: '#f8fafc',
                            border: '1px solid',
                            borderColor: 'divider',
                          }}
                        >
                          <Typography
                            variant="subtitle2"
                            sx={{ fontWeight: 700, color: '#40518A', mb: 1.5 }}
                          >
                            Email template
                          </Typography>

                          <TextField
                            size="small"
                            fullWidth
                            label="Subject"
                            placeholder="Email subject"
                            value={action.mail_subject || ''}
                            onChange={(e) => updateAction(idx, { mail_subject: e.target.value })}
                            sx={{ mb: 1.5 }}
                          />

                          <TextField
                            size="small"
                            fullWidth
                            label="Message body"
                            placeholder="Email message"
                            value={action.mail_body || ''}
                            onChange={(e) => updateAction(idx, { mail_body: e.target.value })}
                            multiline
                            minRows={5}
                            error={Boolean(mailPlaceholderError)}
                          />
                          <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 1 }}>
                            {isWorkstationEvent(form.event)
                              ? 'Placeholders: {workspace}, {workstation}, {idle_duration}. The duration is idle time or occupied time, matching Occupancy status.'
                              : 'Placeholders: {employee}, {time}, {camera}, {candidate_1}/{matching_score_1}, {vendor}, {visitor} and {intrusion} require a matching condition on this rule.'}
                          </Typography>
                          {mailPlaceholderError ? (
                            <Typography variant="caption" color="error" sx={{ display: 'block', mt: 0.5 }}>
                              {mailPlaceholderError}
                            </Typography>
                          ) : null}
                        </Box>
                      </Stack>
                    )}

                    {action.action_type === 'send_whatsapp' && (
                      <Stack spacing={2} sx={{ mt: 2 }}>
                        <WhatsAppRecipientsInput
                          recipients={parseWhatsAppRecipients(action.recipients ?? action.value)}
                          onChange={(recipients) => updateAction(idx, { recipients })}
                        />

                        <Box
                          sx={{
                            p: 2,
                            borderRadius: 1.5,
                            bgcolor: '#f8fafc',
                            border: '1px solid',
                            borderColor: 'divider',
                          }}
                        >
                          <Typography
                            variant="subtitle2"
                            sx={{ fontWeight: 700, color: '#40518A', mb: 1.5 }}
                          >
                            WhatsApp template
                          </Typography>

                          {loadingWhatsAppTemplates ? (
                            <Stack direction="row" spacing={1} alignItems="center" sx={{ py: 1 }}>
                              <CircularProgress size={18} />
                              <Typography variant="body2" color="text.secondary">
                                Loading approved templates…
                              </Typography>
                            </Stack>
                          ) : (
                            <FormControl size="small" fullWidth sx={{ mb: 1.5 }}>
                              <InputLabel id={`wa-template-label-${action.id}`} shrink>
                                Approved template
                              </InputLabel>
                              <Select
                                labelId={`wa-template-label-${action.id}`}
                                label="Approved template"
                                value={action.whatsapp_template_id || ''}
                                displayEmpty
                                renderValue={(selected) => {
                                  if (!selected) return '';
                                  const tpl = whatsappTemplates.find(
                                    (t) => String(t.id) === String(selected)
                                  );
                                  return tpl?.name || action.whatsapp_template_name || selected;
                                }}
                                onChange={(e) => {
                                  const tpl = whatsappTemplates.find(
                                    (t) => String(t.id) === String(e.target.value)
                                  );
                                  updateAction(idx, {
                                    whatsapp_template_id: String(tpl?.id || ''),
                                    whatsapp_template_name: String(tpl?.name || ''),
                                    whatsapp_template_db_id: tpl?.db_id ?? null,
                                    whatsapp_template_user_selected: true,
                                  });
                                }}
                              >
                                {whatsappTemplates.map((tpl) => (
                                  <MenuItem key={tpl.id} value={tpl.id}>
                                    {tpl.name}
                                  </MenuItem>
                                ))}
                              </Select>
                            </FormControl>
                          )}

                          {whatsappTemplatesError ? (
                            <Typography variant="caption" color="error" sx={{ display: 'block', mb: 1 }}>
                              {whatsappTemplatesError}
                            </Typography>
                          ) : null}

                          {!loadingWhatsAppTemplates && whatsappTemplates.length === 0 && !whatsappTemplatesError ? (
                            <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 1 }}>
                              No approved WhatsApp templates found. Configure CRM_INBOX_ID and approve templates in CRM.
                            </Typography>
                          ) : null}

                          <TextField
                            size="small"
                            fullWidth
                            label="Template preview"
                            value={getWhatsAppTemplatePreview(
                              whatsappTemplates.find(
                                (t) => String(t.id) === String(action.whatsapp_template_id)
                              )
                            )}
                            multiline
                            minRows={4}
                            InputProps={{ readOnly: true }}
                            placeholder="Select a template to preview its message"
                          />
                        </Box>
                      </Stack>
                    )}

                    {action.action_type === 'play_speaker' && (
                      <Stack spacing={1.5} sx={{ mt: 2 }}>
                        {loadingAlertSounds || loadingSpeakers ? (
                          <Stack direction="row" spacing={1} alignItems="center">
                            <CircularProgress size={18} />
                            <Typography variant="body2" color="text.secondary">
                              Loading speakers and alert sounds…
                            </Typography>
                          </Stack>
                        ) : (
                          <Stack spacing={1.5}>
                            <FormControl size="small" fullWidth>
                              <InputLabel id={`speaker-label-${action.id}`} shrink>
                                Speaker
                              </InputLabel>
                              <Select
                                labelId={`speaker-label-${action.id}`}
                                label="Speaker"
                                value={action.speaker_id || ''}
                                displayEmpty
                                renderValue={(selected) => {
                                  if (!selected) return '';
                                  const sp = speakers.find((s) => String(s.id) === String(selected));
                                  return sp?.label || selected;
                                }}
                                onChange={(e) => updateAction(idx, { speaker_id: e.target.value })}
                              >
                                {speakers.map((sp) => (
                                  <MenuItem key={sp.id} value={sp.id}>
                                    {sp.label || sp.host || sp.id}
                                  </MenuItem>
                                ))}
                              </Select>
                            </FormControl>
                            <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1} alignItems={{ sm: 'center' }}>
                            <FormControl size="small" sx={{ minWidth: 220, flex: 1 }}>
                              <InputLabel id={`sound-label-${action.id}`} shrink>
                                Alert sound
                              </InputLabel>
                              <Select
                                labelId={`sound-label-${action.id}`}
                                label="Alert sound"
                                value={action.value || ''}
                                displayEmpty
                                renderValue={(selected) => selected || ''}
                                onChange={(e) => {
                                  setPlayingSound(null);
                                  setPlayingSpeakerSound(null);
                                  updateAction(idx, { value: e.target.value });
                                }}
                              >
                                {alertSounds.map((sound) => (
                                  <MenuItem key={sound.filename} value={sound.filename}>
                                    {sound.filename}
                                  </MenuItem>
                                ))}
                              </Select>
                            </FormControl>
                            <Button
                              size="small"
                              variant="outlined"
                              startIcon={action.value && playingSound === action.value ? <CircularProgress size={14} /> : <PlayArrowIcon />}
                              disabled={!action.value || playingSound === action.value}
                              onClick={() => previewAlertSound(action.value)}
                              sx={{ textTransform: 'none', whiteSpace: 'nowrap' }}
                            >
                              Play
                            </Button>
                            <Button
                              size="small"
                              variant="outlined"
                              startIcon={action.value && playingSpeakerSound === action.value ? <CircularProgress size={14} /> : <PlayArrowIcon />}
                              disabled={!action.value || !action.speaker_id || playingSpeakerSound === action.value}
                              onClick={() => previewAlertSoundOnSpeaker(action.value, action.speaker_id)}
                              sx={{ textTransform: 'none', whiteSpace: 'nowrap' }}
                            >
                              Play on speaker
                            </Button>
                            </Stack>
                          </Stack>
                        )}
                        {speakersError ? (
                          <Typography variant="caption" color="error">
                            {speakersError}
                          </Typography>
                        ) : null}
                        {!loadingSpeakers && speakers.length === 0 && !speakersError ? (
                          <Typography variant="caption" color="text.secondary">
                            No speakers configured. Add one on Speaker configuration.
                          </Typography>
                        ) : null}
                        {alertSoundsError ? (
                          <Typography variant="caption" color="error">
                            {alertSoundsError}
                          </Typography>
                        ) : null}
                        {!loadingAlertSounds && !alertSoundsConfigured ? (
                          <Typography variant="caption" color="text.secondary">
                            Set ALERT_SPEAKER_DIR and add .wav files on the server.
                          </Typography>
                        ) : null}
                        {!loadingAlertSounds && alertSoundsConfigured && alertSounds.length === 0 && !alertSoundsError ? (
                          <Typography variant="caption" color="text.secondary">
                            No .wav files found in ALERT_SPEAKER_DIR.
                          </Typography>
                        ) : null}
                      </Stack>
                    )}
                  </Box>
              ))}

              {/* Add Action */}
              <Box sx={{ p: 1.5, bgcolor: '#f8fafc', borderTop: '1px solid', borderColor: 'divider' }}>
                <Button
                  size="small"
                  startIcon={<AddIcon />}
                  onClick={addAction}
                  sx={{ textTransform: 'none', fontWeight: 600, color: '#40518A' }}
                >
                  Add Action
                </Button>
              </Box>
            </Box>
          </Box>
        </Stack>
      </DialogContent>

      <DialogActions
        sx={{
          px: { xs: 2, sm: 3 },
          py: 2,
          gap: 1,
          flexDirection: { xs: 'column-reverse', sm: 'row' },
          alignItems: { xs: 'stretch', sm: 'center' },
        }}
      >
        <Button onClick={onClose} variant="outlined" fullWidth={isMobile}>Cancel</Button>
        <Button
          variant="contained"
          onClick={onSave}
          disabled={!valid}
          fullWidth={isMobile}
          sx={{ minWidth: { sm: 100 } }}
        >
          {mode === 'edit' ? 'Update' : 'Create'}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default RuleModal;
